import React from 'react'
import styled from 'styled-components'

import { breakpoints, colors } from '@/styles'
import {
  Accordion,
  AccordionPanel,
  AccordionTitle
} from '@/components/accordion'
import { DownArrowWhiteIcon } from '@/components/icons'
import { Label, ParagraphRegular } from '@/components/text'

const Wrapper = styled.div`
  :first-child {
    margin-top: 70px;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    :first-child {
      margin-top: 0px;
    }
  }
`

const AccordionContainer = styled.section`
  border-bottom: 1px solid #d9d9d9;
  :last-child {
    border-bottom: 0;
  }
`

const AccordionRow = styled.div`
  padding: 10px 40px 10px 12px;
`

const AccordionContent = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors.grey56};
`

const AccordionTitleBar = styled.div<{ active?: boolean }>`
  height: 76px;
  cursor: pointer;
  padding: 0 12px;

  svg path {
    fill: #494949;
  }
  svg {
    transition: transform 0.3s ease-in-out;
    transform: rotate(0deg);
    flex-shrink: 0;

    ${props =>
      props.active &&
      `
      transform: rotate(180deg);
      path {
        fill: ${colors.primary} !important;
      }
    `}
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 10px 12px;
  }
`

const ClickWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
`

const ItemContainer = styled.div`
  display: inline-block;
  padding-bottom: 20px;

  > :not(:last-child) {
    margin-bottom: 16px;
  }
`

const AnswerHeading = styled.p`
  padding-bottom: 12px;
  font-weight: 500;
`

export const ItemLink = styled.a`
  word-wrap: break-word;
  color: ${colors.link};
  padding: 0;
  background: none;
  text-decoration: underline;
`

const isFaqLink = (step: FaqStep): step is FaqLink => {
  return !React.isValidElement(step)
}

type FaqLink = {
  text?: React.ReactNode
  onClick?: any
  href?: string
}

type FaqStep = FaqLink | React.ReactNode

type FaqAnswer = {
  heading?: React.ReactNode
  steps?: FaqStep[]
}

type FaqData = {
  title?: string
  subtitle?: React.ReactNode
  heading?: React.ReactNode
  answers?: FaqAnswer[]
}

type FaqProps = {
  data: FaqData[]
  trackingId?: string
}

const FaqAccordion: React.FC<FaqProps> = ({ data, trackingId }) => {
  return (
    <Wrapper>
      {data.map(tip => (
        <Accordion key={tip.title}>
          <AccordionContainer>
            <AccordionTitle>
              <AccordionTitleBar id={trackingId}>
                <ClickWrapper>
                  <Label type="medium">{tip.title}</Label>
                  <DownArrowWhiteIcon width={15} height={12} />
                </ClickWrapper>
              </AccordionTitleBar>
            </AccordionTitle>
            <AccordionPanel>
              <AccordionRow>
                {tip.answers &&
                  tip.answers.map((answer, answerIndex) => (
                    <AccordionContent key={`${tip.subtitle}-${answerIndex}`}>
                      {answer.heading && (
                        <AnswerHeading>{answer.heading}</AnswerHeading>
                      )}
                      {answer.steps && (
                        <ItemContainer>
                          {answer.steps.map((step, stepIndex) => {
                            if (isFaqLink(step)) {
                              if (step?.href) {
                                return (
                                  <ItemLink
                                    key={`${step}-${stepIndex}`}
                                    href={step.href}
                                    target="_blank"
                                  >
                                    {step.text}
                                  </ItemLink>
                                )
                              } else if (step?.onClick) {
                                return (
                                  <ItemLink
                                    key={`${step}-${stepIndex}`}
                                    onClick={step.onClick}
                                    as="button"
                                    type="button"
                                  >
                                    {step.text}
                                  </ItemLink>
                                )
                              }
                            }
                            return (
                              <ParagraphRegular key={`${step}-${stepIndex}`}>
                                {step as React.ReactNode}
                              </ParagraphRegular>
                            )
                          })}
                        </ItemContainer>
                      )}
                    </AccordionContent>
                  ))}
              </AccordionRow>
            </AccordionPanel>
          </AccordionContainer>
        </Accordion>
      ))}
    </Wrapper>
  )
}

export default FaqAccordion
